import { useEffect, useState } from "react";
import { approvalAPI, documentAPI, userAPI } from "../services/api";
import { Modal, Btn, Th, Td, Toast, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import { MdVisibility, MdCreate, MdWarning, MdSend, MdCancel, MdCheckCircle } from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const STATUS_STYLE = {
  PENDING:  { bg:"#FEF3C7", color:"#92400E", label:"រង់ចាំ"    },
  APPROVED: { bg:"#D1FAE5", color:"#065F46", label:"បានអនុម័ត" },
  REJECTED: { bg:"#FEE2E2", color:"#991B1B", label:"បដិសេធ"    },
};

const inputStyle = {
  width:"100%", padding:"9px 12px",
  border:`1px solid ${BORDER}`, borderRadius:8,
  fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};

const toDisplay = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB"); }
  catch { return iso; }
};

function ApprovalBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg:"#F3F4F6", color:"#6B7280", label: status || "—" };
  return (
    <span style={{ padding:"4px 12px", borderRadius:20, fontSize:11,
                   fontWeight:600, background:s.bg, color:s.color,
                   fontFamily:KH, whiteSpace:"nowrap" }}>
      {s.label}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {Array(7).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:12,
                  padding:"10px 0", borderBottom:`1px solid ${BORDER}` }}>
      <span style={{ fontSize:12, color:MUTED, minWidth:150,
                     flexShrink:0, fontFamily:KH }}>{label}</span>
      <span style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                     flex:1 }}>{value || "—"}</span>
    </div>
  );
}

export default function ApprovalsPage() {
  const { t } = useLanguage();
  const [list,       setList]       = useState([]);
  const [docList,    setDocList]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [statusFil,  setStatusFil]  = useState("");
  const [modal,      setModal]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [form,       setForm]       = useState({});
  const [saving,     setSaving]     = useState(false);
  const [errors,     setErrors]     = useState({});
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [toast,      setToast]      = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await approvalAPI.getAll(page, pageSize, statusFil || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    documentAPI.getAll(0, 200).then(r => {
      const d = r?.data?.data ?? r?.data;
      setDocList(d?.content || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, pageSize, statusFil]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleRequest = async () => {
    if (!form.documentId) {
      setErrors({ documentId:"សូមជ្រើសរើសឯកសារ" });
      return;
    }
    setSaving(true);
    try {
      await approvalAPI.request({
        documentId: parseInt(form.documentId, 10),
        note:       form.note || "",
      });
      await load();
      setToast({ message:t("ស្នើសុំអនុម័តបានជោគជ័យ!"), type:"success" });
      setModal(null); setForm({}); setErrors({});
    } catch (e) {
      try {
        const b = e.body || JSON.parse(e.message);
        if (b?.data) { setErrors(b.data); return; }
      } catch {}
      setToast({ message:errMessage(e), type:"error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDecide = async (status) => {
    setSaving(true);
    try {
      await approvalAPI.decide(selected.approvalId ?? selected.id, {
        statusCode :  status,
        comment: form.comment || "",
      });
      await load();
      setToast({ message:status === "APPROVED" ? "បានអនុម័តជោគជ័យ!" : "បានបដិសេធជោគជ័យ!", type:"success" });
      setModal(null); setForm({}); setSelected(null);
    } catch (e) {
      setToast({ message:errMessage(e), type:"error" });
    } finally {
      setSaving(false);
    }
  };

  const pendingCount  = list.filter(a => (a.status||a.statusCode) === "PENDING").length;
  const approvedCount = list.filter(a => (a.status||a.statusCode) === "APPROVED").length;
  const rejectedCount = list.filter(a => (a.status||a.statusCode) === "REJECTED").length;

  return (
    <div>
      <Toast message={toast?.message} type={toast?.type} onHide={() => setToast(null)} />

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"ការស្នើសុំសរុប", value:totalItems,    color:BROWN      },
          { label:"រង់ចាំអនុម័ត",   value:pendingCount,  color:"#F59E0B"  },
          { label:"បានអនុម័ត",      value:approvedCount, color:"#10B981"  },
          { label:"បដិសេធ",          value:rejectedCount, color:"#EF4444"  },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"14px 18px", border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>
              {s.label}
            </p>
            <p style={{ margin:0, fontSize:26, fontWeight:700,
                        color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <select value={statusFil}
          onChange={e => { setStatusFil(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          <option value="PENDING">{t("រង់ចាំ")}</option>
          <option value="APPROVED">{t("បានអនុម័ត")}</option>
          <option value="REJECTED">{t("បដិសេធ")}</option>
        </select>

        <div style={{ flex:1 }} />

        <button onClick={() => { setErrors({}); setForm({}); setModal("request"); }}
          style={{ padding:"9px 20px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap", fontFamily:KH }}>
          {t("+ ស្នើសុំការអនុម័ត")}
        </button>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("approval_id")}</Th>
              <Th>{t("ឯកសារ")}</Th>
              <Th>{t("ស្នើដោយ")}</Th>
              <Th>{t("ថ្ងៃស្នើ")}</Th>
              <Th>{t("អ្នកអនុម័ត")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ថ្ងៃអនុម័ត")}</Th>
              <Th>{t("សកម្មភាព")}</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4].map(i => <SkeletonRow key={i} />)
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding:"40px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានការស្នើសុំអនុម័ត")}
                </td>
              </tr>
            ) : list.map(a => (
              <tr key={a.approvalId ?? a.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>

                <Td muted>#{a.approvalId ?? a.id}</Td>

                <Td>
                  <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                               marginBottom:2 }}>
                    {a.documentName || `ឯកសារ #${a.documentId}`}
                  </p>
                  {a.documentNumber && (
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      {a.documentNumber}
                    </p>
                  )}
                </Td>

                <Td>{a.requestedByName || a.requestedBy || "—"}</Td>

                <Td muted>{toDisplay(a.requestedAt)}</Td>

                <Td>{a.approvedByName || a.approvedBy || "—"}</Td>

                <Td><ApprovalBadge status={a.status || a.statusCode} /></Td>

                <Td muted>{toDisplay(a.approvedAt)}</Td>

                <td style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <button onClick={() => { setSelected(a); setModal("view"); }}
                      title={t("មើលព័ត៌មាន")}
                      style={{ background:"none", border:"none",
                               cursor:"pointer", color:"#3B82F6",
                               display:"flex", alignItems:"center", padding:"4px" }}>
                      <MdVisibility size={18}/>
                    </button>
                    {(a.status || a.statusCode) === "PENDING" && (
                      <button
                        onClick={() => {
                          setSelected(a);
                          setForm({ comment:"" });
                          setModal("decide");
                        }}
                        style={{ padding:"4px 12px", background:"#EDE9FE",
                                 border:"1px solid #C4B5FD", borderRadius:6,
                                 cursor:"pointer", fontSize:12,
                                 color:"#5B21B6", fontFamily:KH,
                                 display:"flex", alignItems:"center", gap:4 }}>
                        <MdCreate size={13}/> {t("សម្រេច")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </TableScroll>

        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      {modal === "request" && (
        <Modal
          title={t("ស្នើសុំការអនុម័ត")}
          subtitle={t("ជ្រើសរើសឯកសារដែលត្រូវការអនុម័ត")}
          onClose={() => { setModal(null); setForm({}); setErrors({}); }}
        >
          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>
              {t("ឯកសារ (document_id)")}
              <span style={{ color:"#EF4444", marginLeft:3 }}>*</span>
            </label>
            <select value={form.documentId||""}
              onChange={e => { f("documentId",e.target.value); setErrors({}); }}
              style={errors.documentId
                ? { ...inputStyle, border:"1.5px solid #EF4444", background:"#FFF5F5" }
                : inputStyle}>
              <option value="">{t("ជ្រើសរើសឯកសារ...")}</option>
              {docList.map(d => (
                <option key={d.documentId ?? d.id}
                  value={String(d.documentId ?? d.id)}>
                  {d.documentName || `ឯកសារ #${d.documentId ?? d.id}`}
                  {d.documentNumber ? ` — ${d.documentNumber}` : ""}
                </option>
              ))}
            </select>
            {errors.documentId && (
              <p style={{ fontSize:11, color:"#EF4444", marginTop:3, fontFamily:KH, display:"flex", alignItems:"center", gap:3 }}>
                <MdWarning size={13}/> {errors.documentId}
              </p>
            )}
          </div>

          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>
              {t("កំណត់ត្រា (note)")}
            </label>
            <textarea value={form.note||""}
              onChange={e => f("note", e.target.value)}
              rows={3}
              placeholder={t("បញ្ចូលមូលហេតុ ឬ ព័ត៌មានបន្ថែម (ស្រេចចិត្ត)...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setErrors({}); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleRequest} style={{ opacity:saving ? 0.7 : 1 }}>
              {saving ? "កំពុងស្នើ..." : <span style={{display:"flex",alignItems:"center",gap:5}}><MdSend size={14}/>{t("ស្នើសុំ")}</span>}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "decide" && selected && (
        <Modal
          title={t("សម្រេចការអនុម័ត")}
          subtitle={selected.documentName || `ឯកសារ #${selected.documentId}`}
          onClose={() => { setModal(null); setForm({}); setSelected(null); }}
        >
          <div style={{ background:BG, borderRadius:10,
                        border:`1px solid ${BORDER}`,
                        padding:"14px 16px", marginBottom:16 }}>
            <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                        marginBottom:10, fontFamily:KH }}>
              {t("ព័ត៌មានការស្នើសុំ")}
            </p>
            <InfoRow label={t("លេខការអនុម័ត")}
              value={`#${selected.approvalId ?? selected.id}`} />
            <InfoRow label={t("ឯកសារ")}
              value={selected.documentName || `#${selected.documentId}`} />
            <InfoRow label={t("ស្នើដោយ")}
              value={selected.requestedByName || selected.requestedBy} />
            <InfoRow label={t("ថ្ងៃស្នើ")}
              value={toDisplay(selected.requestedAt)} />
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>
              {t("មតិយោបល់")}
            </label>
            <textarea value={form.comment||""}
              onChange={e => f("comment", e.target.value)}
              rows={3}
              placeholder={t("បញ្ចូលមូលហេតុ ឬ មតិបន្ថែម...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <button onClick={() => handleDecide("REJECTED")}
              disabled={saving}
              style={{ padding:"14px", background:"#FEF2F2",
                       border:"2px solid #EF4444", borderRadius:10,
                       cursor:saving?"not-allowed":"pointer",
                       fontSize:14, fontWeight:700, color:"#991B1B",
                       fontFamily:KH, opacity:saving ? 0.7 : 1,
                       transition:"all 0.15s",
                       display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <MdCancel size={18}/> {t("បដិសេធ")}
            </button>
            <button onClick={() => handleDecide("APPROVED")}
              disabled={saving}
              style={{ padding:"14px", background:"#F0FDF4",
                       border:"2px solid #10B981", borderRadius:10,
                       cursor:saving?"not-allowed":"pointer",
                       fontSize:14, fontWeight:700, color:"#065F46",
                       fontFamily:KH, opacity:saving ? 0.7 : 1,
                       transition:"all 0.15s",
                       display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <MdCheckCircle size={18}/> {t("អនុម័ត")}
            </button>
          </div>

          <div style={{ marginTop:12, textAlign:"center" }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setSelected(null); }}>
              {t("បោះបង់")}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={`ការអនុម័ត #${selected.approvalId ?? selected.id}`}
          subtitle={<ApprovalBadge status={selected.status || selected.statusCode} />}
          onClose={() => setModal(null)}
        >
          <InfoRow label={t("លេខការអនុម័ត")}
            value={`#${selected.approvalId ?? selected.id}`} />
          <InfoRow label={t("ឯកសារ")}
            value={selected.documentName || `#${selected.documentId}`} />
          <InfoRow label={t("ស្នើដោយ")}
            value={selected.requestedByName || selected.requestedBy} />
          <InfoRow label={t("ថ្ងៃស្នើ")}
            value={toDisplay(selected.requestedAt)} />
          <InfoRow label={t("អនុម័តដោយ")}
            value={selected.approvedByName || selected.approvedBy} />
          <InfoRow label={t("ស្ថានភាព")}
            value={<ApprovalBadge status={selected.status || selected.statusCode} />} />
          <InfoRow label={t("មតិយោបល់")}
            value={selected.comment} />
          <InfoRow label={t("ថ្ងៃអនុម័ត")}
            value={toDisplay(selected.approvedAt)} />

          {(selected.status || selected.statusCode) === "PENDING" && (
            <div style={{ marginTop:16 }}>
              <button onClick={() => { setForm({ comment:"" }); setModal("decide"); }}
                style={{ width:"100%", padding:"10px", background:BROWN,
                         color:"#fff", border:"none", borderRadius:8,
                         cursor:"pointer", fontSize:13, fontFamily:KH,
                         display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                <MdCreate size={15}/> {t("ធ្វើការសម្រេច")}
              </button>
            </div>
          )}

          <div style={{ marginTop:12, display:"flex", justifyContent:"flex-end" }}>
            <Btn variant="outline" onClick={() => setModal(null)}>{t("បិទ")}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}